import React, { FC, useState } from 'react';
import { Switch, Route } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { push } from 'redux-first-history';
import { connect } from 'react-redux';
import { compose } from 'react-recompose';

import styled from 'styled-components';

import { TabsPanel, PrimaryButton, Container, Header } from '@dev/ee-components';
import { safelyDecodeBase64Url } from '@dev/ee-tools';

import Overview from './Overview';
import LinkTracking from './LinkTracking';
import SingleChannelLogs from './EmailLog';
import UpIcon from './upIcon.svg';

import SingleChannelProps from './SingleChannelInterface';
import { useMobile } from '@api/services/hooks/useMobile';
import { StatisticsPaths, pathsParams } from '@api/services/dictionaries/paths';

const dispatchProps = { Push: push };
type DispatchProps = {
    Push(url: string): void;
};

type Props = DispatchProps & SingleChannelProps;

const Content = styled.div`
    margin-top: 24px;
`;

const SingleChannelTabs: FC<Props> = (props: Props): JSX.Element => {
    const { t, match, Push } = props;
    const mobilePadding = {
        left: 16,
        right: 16,
    };
    const channelName = match?.params?.channelName;
    const isMobile = useMobile();
    const channelOverviewUrl = StatisticsPaths.StatisticsChannelOverview.replace(pathsParams.channelName, channelName);
    const channelLogsUrl = StatisticsPaths.StatisticsChannelLogs.replace(pathsParams.channelName, channelName);
    const channelLinkTrackingsUrl = StatisticsPaths.StatisticsChannelLinkTracking.replace(pathsParams.channelName, channelName);

    let firstTabValue = 0;
    if (match.url === channelLogsUrl) {
        firstTabValue = 1;
    } else if (match.url === channelLinkTrackingsUrl) {
        firstTabValue = 2;
    }
    const [tabValue, setTabValue] = useState(firstTabValue);
    const [clickLinksExport, setClickLinksExport] = useState(false);
    const [clickLogsExport, setClickLogsExport] = useState(false);
    const [disableLogsExport, setDisableLogsExport] = useState(true);
    const [disableLinksExport, setDisableLinksExport] = useState(true);

    const tabsOnClick = (ev, newValue): void => {
        setTabValue(newValue);

        switch (newValue) {
            case 0:
                Push(channelOverviewUrl);
                break;
            case 1:
                Push(channelLogsUrl);
                break;
            case 2:
                Push(channelLinkTrackingsUrl);
                break;
        }
    };

    const handleLinksExport = (): void => {
        setClickLinksExport(true);
    };

    const handleLogsExport = (): void => {
        setClickLogsExport(true);
    };

    const navigateToChannels = (): void => {
        const { Push } = props;
        Push(StatisticsPaths.StatisticsChannels);
    };

    const renderNavigation = (): JSX.Element => {
        return (
            <div>
                <Header
                    header={{
                        title: safelyDecodeBase64Url(match?.params?.channelName),
                    }}
                    breadcrumbs={{
                        text: t('channels'),
                        onClick: navigateToChannels,
                    }}
                    customContent={
                        <>
                            {!isMobile && tabValue === 1 ? (
                                <PrimaryButton
                                    id="exportLog"
                                    onClick={handleLogsExport}
                                    disabled={disableLogsExport}
                                    size="medium"
                                    text={t('export')}
                                    variant="text"
                                    startIcon={<UpIcon />}
                                />
                            ) : null}
                            {!isMobile && tabValue === 2 ? (
                                <PrimaryButton
                                    id="exportlinks"
                                    onClick={handleLinksExport}
                                    disabled={disableLinksExport}
                                    size="medium"
                                    text={t('export')}
                                    variant="text"
                                    startIcon={<UpIcon />}
                                />
                            ) : null}
                        </>
                    }
                    customTitleColumnSize={tabValue === 1 || tabValue === 2 ? 10 : 12}
                />
            </div>
        );
    };

    const handleDisableLogExport = (isDisabled): void => {
        setDisableLogsExport(isDisabled);
    };

    const handleDisableLinksExport = (isDisabled): void => {
        setDisableLinksExport(isDisabled);
    };

    return (
        <Container variant="full" paddingMobile={mobilePadding}>
            {renderNavigation()}

            <TabsPanel
                tabs={[{ label: t('overview') }, { label: t('emaillogs') }, { label: t('linktracking') }]}
                onChange={tabsOnClick}
                value={tabValue}
            />
            <Content>
                <Switch>
                    <Route path={StatisticsPaths.StatisticsChannelOverview} exact component={Overview} />
                    <Route
                        path={StatisticsPaths.StatisticsChannelLogs}
                        exact
                        render={(props): JSX.Element => (
                            <SingleChannelLogs disabledLogsExport={handleDisableLogExport} clickExport={clickLogsExport} {...props} />
                        )}
                    />
                    <Route
                        path={StatisticsPaths.StatisticsChannelLinkTracking}
                        exact
                        render={(props): JSX.Element => (
                            <LinkTracking disabledExport={handleDisableLinksExport} clickExport={clickLinksExport} {...props} />
                        )}
                    />
                </Switch>
            </Content>
        </Container>
    );
};

export default compose(withTranslation(), connect<SingleChannelProps, DispatchProps>(null, dispatchProps))(SingleChannelTabs);
